import { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../context/auth-context';
import AreasAPI from '../../../services/api/admin/areas-api';
import Logger from '../../../utils/logger';
import { DataGrid, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { SimpleItemModal } from '../shared/SimpleItemModal';

export const Areas = ({ onClose, currentProject }) => {
  const [areas, setAreas] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentArea, setCurrentArea] = useState({
    id: '',
    name: '',
    description: '',
    project_id: '',
  });
  const [editArea, setEditArea] = useState(false);

  const auth = useContext(AuthContext);

  const getAreas = useCallback(async () => {
    try {
      const areasData = await AreasAPI.getByProject(
        currentProject.id,
        auth.token
      );
      Logger.info('Areas retrieved successfully', { count: areasData?.length });
      setAreas(areasData);
    } catch (err) {
      alert('There was an error getting the areas');
      Logger.error('Error getting the areas', err);
    }
  }, [currentProject, auth.token]);

  useEffect(() => {
    if (currentProject && currentProject.id && auth.token) {
      getAreas();
    }
  }, [currentProject, auth.token, getAreas]);

  const handleOpen = () => {
    setCurrentArea({
      id: '',
      name: '',
      description: '',
      project_id: currentProject.id,
    });
    setEditArea(false);
    setOpen(true);
  };

  const handleAddArea = async (area) => {
    const newArea = { ...area, project_id: currentProject.id };
    Logger.userAction('Adding new area', { area: newArea });

    setEditArea(false);
    setOpen(false);

    try {
      const created = await AreasAPI.create(newArea, auth.token);
      Logger.info('Area added successfully', { area: newArea });
      // Refresh the list to get the actual data from the database (with the auto-generated ID)
      getAreas();
      // In a project with ACC asset sync on, the area is also created as an ACC
      // category (ticket #257). That never blocks the save, but say if it failed.
      if (created?.accCategorySync?.success === false) {
        Logger.info('Area created but its ACC category setup failed', {
          area: newArea,
          error: created.accCategorySync.error,
        });
        alert(
          `Area "${newArea.name}" was created, but it could not be set up in ACC:\n\n` +
            `${created.accCategorySync.error}\n\n` +
            'It will be set up the next time a package in this area syncs to ACC.'
        );
      }
    } catch (err) {
      Logger.error('Error adding area to backend', err);
      alert(
        err.response?.data?.message || 'There was an error adding the area'
      );
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleDeleteArea = async (id) => {
    if (window.confirm('Do you want to delete this area?')) {
      const oldAreas = areas;
      setAreas(areas.filter((area) => area.id !== id));

      try {
        await AreasAPI.delete(id, currentProject.id, auth.token);
        Logger.info('Area deleted successfully', { areaId: id });
        // Refresh the list
        getAreas();
      } catch (err) {
        Logger.error('Error deleting area in backend', err);
        alert(err.response?.data?.message || 'Error deleting area');
        setAreas(oldAreas);
      }
    }
  };

  const handleUpdateArea = async (updatedArea) => {
    Logger.userAction('Updating area', { area: updatedArea });
    setOpen(false);
    const previousAreas = areas;
    const updatedAreas = areas.map((area) => {
      if (area.id === updatedArea.id) {
        return { ...updatedArea, project_id: currentProject.id };
      } else {
        return area;
      }
    });

    try {
      const modifiedArea = await AreasAPI.update(
        { ...updatedArea, project_id: currentProject.id },
        auth.token
      );
      Logger.info('Area updated successfully', { area: modifiedArea });
      // Only update state if API call succeeds, then refresh from server
      setAreas(updatedAreas);
      getAreas();
    } catch (err) {
      Logger.error('Error updating area', err);
      alert(err.response?.data?.message || 'We were unable to update the area');
      setAreas(previousAreas);
    }
  };

  const handleEditArea = (id) => {
    const area = id.row;
    setCurrentArea({
      id: area.id,
      name: area.name,
      description: area.description || '',
      project_id: currentProject.id,
    });
    setEditArea(true);
    setOpen(true);
    Logger.userAction('Editing area', { areaId: id.row });
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Area Name',
      width: 200,
      editable: false,
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 400,
      editable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      headerName: '',
      cellClassName: 'actions',
      getActions: (id) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          name="edit"
          label="Edit"
          onClick={() => {
            handleEditArea(id);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon color="action" />}
          name="delete"
          label="Delete"
          onClick={() => {
            handleDeleteArea(id.id);
          }}
          color="inherit"
        />,
      ],
    },
  ];

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            Manage Areas -{' '}
            {currentProject
              ? `${currentProject.number} - ${currentProject.name}`
              : ''}
          </span>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            paddingTop: 2,
          }}
        >
          <Button variant="contained" onClick={() => handleOpen()}>
            Add Area
          </Button>
          <SimpleItemModal
            item={currentArea}
            editMode={editArea}
            open={open}
            handleCancel={handleCancel}
            handleAddItem={handleAddArea}
            handleUpdateItem={handleUpdateArea}
            title="Area"
            itemNameLabel="Area Name"
            descriptionLabel="Description"
          />
          <Box
            sx={{
              width: '100%',
              height: 'calc(100% - 60px)',
              paddingTop: 2,
            }}
          >
            <DataGrid
              getRowId={(row) => row.id}
              columns={columns}
              rows={areas}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                },
              }}
              disableDensitySelector={true}
              disableColumnSelector={true}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
